import React, { useState } from 'react';
import { Nav, NavItem, NavLink } from 'reactstrap';
import classnames from 'classnames';
import { StandardContainer } from '../../../../styledComponents/styledComponents';

export default function JwtMetricsHelp() {
   const [activeTab, setActiveTab] = useState('general');

   const tab = (id) => setActiveTab(id);

   return (
      <div className='mt-3'>
         <Nav tabs>
            <NavItem>
               <NavLink className={classnames({ active: activeTab === 'general' })} onClick={() => tab('general')}>
                  General
               </NavLink>
            </NavItem>

            <NavItem>
               <NavLink className={classnames({ active: activeTab === 'duration' })} onClick={() => tab('duration')}>
                  Duration
               </NavLink>
            </NavItem>

            <NavItem>
               <NavLink className={classnames({ active: activeTab === 'usage' })} onClick={() => tab('usage')}>
                  Usage
               </NavLink>
            </NavItem>

            <NavItem>
               <NavLink className={classnames({ active: activeTab === 'geo' })} onClick={() => tab('geo')}>
                  Network & Geo
               </NavLink>
            </NavItem>
            <NavItem>
               <NavLink className={classnames({ active: activeTab === 'agent' })} onClick={() => tab('agent')}>
                  Origin & Agent
               </NavLink>
            </NavItem>

            <NavItem>
               <NavLink
                  className={classnames({ active: activeTab === 'concurrency' })}
                  onClick={() => tab('concurrency')}
               >
                  Max Concurrency
               </NavLink>
            </NavItem>

            <NavItem>
               <NavLink
                  className={classnames({ active: activeTab === 'jwt-standard' })}
                  onClick={() => tab('jwt-standard')}
               >
                  Token Payload Claims
               </NavLink>
            </NavItem>

            <NavItem>
               <NavLink
                  className={classnames({ active: activeTab === 'jwt-custom' })}
                  onClick={() => tab('jwt-custom')}
               >
                  Token Payload Restrictions
               </NavLink>
            </NavItem>
         </Nav>
         <StandardContainer $paddingTopPx={10} $minHeightPx={380} $border='1px solid #c8ced3' $borderTop='none'>
            {activeTab === 'general' && (
               <>
                  <h5>JTI</h5>
                  <p>
                     The JWT ID (<em>jti</em> claim) uniquely identifying the playback token. All metrics in this row
                     are aggregated per token. For legacy tokens without a <em>jti</em> claim, a short ID derived from
                     the token signature is used instead.
                  </p>

                  <h5>User</h5>
                  <p>
                     User name associated with the token, if provided. This usually represents the end user or customer
                     account.
                  </p>

                  <h5>First stream start</h5>
                  <p>Timestamp of the first playback event observed for this token within the selected timeframe.</p>

                  <h5>Last stream end</h5>
                  <p>Timestamp of the last playback event observed for this token within the selected timeframe.</p>
               </>
            )}

            {activeTab === 'jwt-standard' && (
               <>
                  <h5>JTI</h5>
                  <p>
                     Unique token identifier (<em>jti</em> claim).
                  </p>

                  <h5>Issued At / Expiration / Valid Not Before</h5>
                  <p>
                     Timestamps defining the validity period of the token (<em>iat</em>, <em>exp</em>, <em>nbf</em>{' '}
                     claims). All timestamps are shown in UTC.
                  </p>

                  <h5>Audience</h5>
                  <p>
                     Intended audience(s) of the token (<em>aud</em> claim).
                  </p>

                  <h5>User</h5>
                  <p>
                     User identifier associated with the token (<em>sub</em> or <em>user</em> claim), if provided.
                  </p>
               </>
            )}

            {activeTab === 'jwt-custom' && (
               <>
                  <h5>Tag</h5>
                  <p>Optional tag encoded in the token payload.</p>

                  <h5>Domain / IP</h5>
                  <p>Domain and/or IP address to which the token is restricted to.</p>

                  <h5>Streams</h5>
                  <p>List of stream names the token is authorized to access, if specified.</p>

                  <h5>Force Disconnect</h5>
                  <p>
                     When enabled, active playback sessions using this token are automatically terminated once the token
                     expires.
                  </p>

                  <h5>Custom Data (cust)</h5>
                  <p>
                     Custom data field encoded in the token payload. The value is base64-decoded if possible and
                     displayed in full width. Can contain arbitrary customer-defined data such as user metadata or
                     session context.
                  </p>
               </>
            )}

            {activeTab === 'duration' && (
               <>
                  <h5>Duration / Sum</h5>
                  <p>
                     Total accumulated playback duration generated using this Playback Token. Includes all sessions and
                     streams.
                  </p>

                  <h5>Duration / Avg</h5>
                  <p>
                     Average playback duration per session. Calculated as total duration divided by the number of
                     sessions.
                  </p>
               </>
            )}

            {activeTab === 'usage' && (
               <>
                  <h5>Sessions</h5>
                  <p>Number of unique playback sessions initiated with this Playback Token.</p>

                  <h5>Streams</h5>
                  <p>Number of distinct streams accessed using this token.</p>

                  <h5>IPs</h5>
                  <p>
                     Number of unique client IP addresses observed using this token. A high value may indicate token
                     sharing.
                  </p>

                  <h5>Tags</h5>
                  <p>Number of unique tags associated with playback sessions for this token.</p>
               </>
            )}

            {activeTab === 'geo' && (
               <>
                  <h5>Top Providers</h5>
                  <p>
                     Top 10 providers from which playback using this Playback Token was most frequently detected (ISP /
                     ASN).
                  </p>

                  <h5>Top Countries</h5>
                  <p>Top 10 countries from which playback using this Playback Token was geo-located.</p>
               </>
            )}
            {activeTab === 'agent' && (
               <>
                  <h5>Top Referrers</h5>
                  <p>Top 10 referrers observed for playback using this Playback Token.</p>

                  <h5>Top Browser Agent IDs</h5>
                  <p>Top 10 browser agent IDs observed for playback using this Playback Token.</p>
               </>
            )}

            {activeTab === 'concurrency' && (
               <>
                  <h5>Max Concurrency</h5>
                  <p>Indicates how many streams were used simultaneously with this Playback Token.</p>
                  <p>
                     The selected timeframe is divided into 10-minute intervals. For each interval, the average
                     concurrency is calculated as total viewing duration divided by the effective time span. The
                     displayed value is the peak (highest) average concurrency across all intervals.
                  </p>
                  <details>
                     <summary style={{ cursor: 'pointer', marginBottom: '6px' }}>Show formula</summary>
                     <pre style={{ background: '#f5f5f5', padding: '12px', borderRadius: '4px', fontSize: '13px' }}>
                        {`timeSpan    = min((streamEnd - streamStart) + avgLogDuration, 600s)
concurrency = durationSum / timeSpan

durationSum      = sum of all reported viewing durations
                   within the 10-minute interval
streamEnd        = timestamp of the last log event
streamStart      = timestamp of the first log event
avgLogDuration   = durationSum / doc_count
                   (accounts for variable log intervals,
                   e.g. short first heartbeat < 60s)
600s             = bucket width cap (10-minute interval)
                   prevents overestimation when sessions
                   span the full interval

displayed value  = max(concurrency) across all intervals`}
                     </pre>
                  </details>
                  <p>
                     Values greater than 1 indicate parallel playback and may suggest token sharing, proxies, or
                     automated usage.
                  </p>
               </>
            )}
         </StandardContainer>
      </div>
   );
}
